import { MessageSquare } from "lucide-react";
import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import Chat from "../AdminPortal/CRM-approval/Chat";

const ApplicationStatus = ({ application }) => {
  const [activeTab, setActiveTab] = useState("application");
  const [staffUserId, setStaffUserId] = useState(null);

  // Safely extract staffUserId when application data is available
  useEffect(() => {
    if (application && application.assignedTo && application.assignedTo._id) {
      setStaffUserId(application.assignedTo._id);
    } else {
    }
  }, [application]);

  const getFormattedDate = (dateString) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "dd MMM, yyyy");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  const getFormattedDateTime = (dateString) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "dd MMM, yyyy - h:mm a");
    } catch (error) {
      console.error("Error formatting date time:", error);
      return "";
    }
  };

  // Don't proceed with timeline entries if application is not available
  if (!application) {
    return (
      <div className="bg-[#131313] rounded-lg p-4 mb-4">
        <p className="text-gray-400 text-center">
          Loading application status...
        </p>
      </div>
    );
  }

  const timelineEntries = [];

  // 1. Lead Created
  if (application?.createdAt) {
    timelineEntries.push({
      status: "new-leads",
      description: "Lead Created",
      details: [
        "User started the application.",
        getFormattedDateTime(application.createdAt),
      ],
      timestamp: application.createdAt,
    });
  }

  // 2. Lead Assigned
  if (application?.assignedTo?.name) {
    timelineEntries.push({
      status: "assigned",
      description: `Lead Assigned to ${application.assignedTo.name}`,
      details: [
        `${application.assignedTo.name} assigned this lead.`,
        getFormattedDateTime(application.updatedAt || application.createdAt),
      ],
      timestamp: application.updatedAt || application.createdAt,
    });
  }

  // 3. Application Approved
  if (application?.status === "approved") {
    timelineEntries.push({
      status: "approved",
      description: "Application sent to Credit Manager",
      details: [
        application?.approvedBy?.name
          ? `Approved by ${application.approvedBy.name}.`
          : "Application approved.",
        getFormattedDateTime(application.approvedAt || application.updatedAt),
      ],
      timestamp:
        application.approvedAt ||
        application.updatedAt ||
        application.createdAt,
    });
  }

  // 4. Processing - Internal
  if (
    application?.status === "processing" &&
    application?.processingMethod === "Internal"
  ) {
    timelineEntries.push({
      status: "processing-internal",
      description: "Lead Verification Started",
      details: [
        "Internal verification started by staff.",
        getFormattedDateTime(application.updatedAt || application.createdAt),
      ],
      timestamp: application.updatedAt || application.createdAt,
    });
  }

  // 5. Processing - External
  if (
    application?.status === "processing" &&
    application?.processingMethod === "External"
  ) {
    timelineEntries.push({
      status: "processing-external",
      description: "Lead moved to Banking Partner",
      details: [
        "Application sent to external banking partner.",
        getFormattedDateTime(application.updatedAt || application.createdAt),
      ],
      timestamp: application.updatedAt || application.createdAt,
    });
  }

  // 6. Document Rejected
  if (application?.status === "rejected") {
    timelineEntries.push({
      status: "rejected",
      description: "Document Rejected",
      details: [
        application?.rejectedBy?.name
          ? `Rejected by ${application.rejectedBy.name}.`
          : "Documents rejected.",
        getFormattedDateTime(application.updatedAt || application.createdAt),
      ],
      timestamp: application.updatedAt || application.createdAt,
    });
  }

  // 7. Documents Reuploaded
  const reuploadEntry = application?.statusHistory?.find((entry) =>
    entry.description?.toLowerCase().includes("re-uploaded")
  );

  if (application?.status === "in-review" && reuploadEntry) {
    timelineEntries.push({
      status: "re-uploaded",
      description: "Documents Re-uploaded",
      details: [
        "User re-uploaded documents.",
        reuploadEntry.timestamp
          ? getFormattedDateTime(reuploadEntry.timestamp)
          : null,
      ],
      timestamp:
        reuploadEntry.timestamp ||
        application.updatedAt ||
        application.createdAt,
    });
  }

  // Final: Sort descending by timestamp
  timelineEntries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  // Get dot color based on status
  const getDotColor = (status) => {
    if (status === "rejected") return "bg-red-500";
    if (status === "processing-external") return "bg-green-500";
    return "bg-[#FFCA20]";
  };

  return (
    <div className="bg-[#131313] rounded-lg p-4 mb-4">
      {/* Tabs */}
      <div className="flex flex-wrap justify-between mb-4 gap-2">
        <button
          onClick={() => setActiveTab("application")}
          className={`px-4 py-2 rounded-full flex items-center ${
            activeTab === "application"
              ? "bg-[#FFCA20] text-black"
              : "text-[#ffca20] border-2 border-[#ffca20]"
          }`}
        >
          <MessageSquare className="w-4 h-4 mr-1" />
          Application
        </button>

        <button
          onClick={() => setActiveTab("chat")}
          className={`px-4 py-2 rounded-full flex items-center ${
            activeTab === "chat"
              ? "bg-[#FFCA20] text-black"
              : "text-[#ffca20] border-2 border-[#ffca20]"
          }`}
        >
          <MessageSquare className="w-4 h-4 mr-1" />
          Chat
        </button>
      </div>

      {/* Timeline Section */}
      {activeTab === "application" ? (
        <div className="pl-6 relative">
          {/* Timeline vertical line */}
          <div className="absolute left-2 top-2 bottom-0 w-0.5 bg-gray-700"></div>

          {timelineEntries.length > 0 ? (
            timelineEntries.map((entry, index) => (
              <div key={index} className="mb-6 relative">
                {/* Colored Dot */}
                <div
                  className={`absolute -left-6 mt-1 w-4 h-4 rounded-full ${getDotColor(
                    entry.status
                  )}`}
                ></div>

                {/* Description */}
                <p className="text-white text-md font-medium">
                  {entry.description}{" "}
                  <span className="text-gray-400">
                    {getFormattedDate(entry.timestamp)}
                  </span>
                </p>

                {/* Details */}
                {entry.details.filter(Boolean).map((detail, i) => (
                  <p key={i} className="text-gray-400 text-sm">
                    {detail}
                  </p>
                ))}
              </div>
            ))
          ) : (
            <p className="text-gray-400 text-sm">
              No timeline entries available.
            </p>
          )}
        </div>
      ) : (
        <Chat
          staffUserId={staffUserId}
          customerId={application?.user?._id}
          senderType="Staff"
          receiverType="Customer"
        />
      )}
    </div>
  );
};

export default ApplicationStatus;
